import React from 'react';
import { Result, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { usePermission } from '../hooks/usePermission';
import { Module, PermissionAction } from '../constants/permissions';

interface PermissionGuardProps {
  children: React.ReactNode;
  // 單一權限檢查
  permission?: string;
  // 模組權限檢查
  module?: Module | string;
  action?: PermissionAction;
  // 多個權限檢查
  permissions?: string[];
  requireAll?: boolean; // 是否需要滿足所有權限，預設為 false（任一滿足即可）
  // 自訂無權限顯示內容
  fallback?: React.ReactNode;
  // 無權限時是否顯示錯誤頁面，預設為 true
  showError?: boolean;
}

const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  permission,
  module,
  action,
  permissions,
  requireAll = false,
  fallback,
  showError = true,
}) => {
  const navigate = useNavigate();
  const { hasPermission, hasModulePermission, hasAnyPermission, hasAllPermissions } = usePermission();

  // 檢查權限
  let hasRequiredPermission = false;

  if (permission) {
    // 單一權限檢查
    hasRequiredPermission = hasPermission(permission);
  } else if (module && action) {
    // 模組權限檢查
    hasRequiredPermission = hasModulePermission({ module, action });
  } else if (permissions && permissions.length > 0) {
    // 多個權限檢查
    hasRequiredPermission = requireAll 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
  } else {
    // 沒有指定權限要求，預設允許
    hasRequiredPermission = true;
  }

  // 如果有權限，顯示子元件
  if (hasRequiredPermission) {
    return <>{children}</>;
  }

  // 如果有自訂無權限顯示內容
  if (fallback) {
    return <>{fallback}</>;
  }

  // 如果不顯示錯誤頁面，返回 null
  if (!showError) {
    return null;
  }

  // 預設無權限頁面
  return (
    <Result
      status="403"
      title="403"
      subTitle="抱歉，您沒有權限訪問此頁面。"
      extra={
        <Button type="primary" onClick={() => navigate(-1)}>
          返回上一頁
        </Button>
      }
    />
  );
};

export default PermissionGuard;

// 便利元件：只在有權限時顯示
export const PermissionShow: React.FC<PermissionGuardProps> = (props) => {
  return <PermissionGuard {...props} showError={false} />;
};

// HOC：為元件添加權限檢查
export const withPermission = <P extends object>(
  Component: React.ComponentType<P>,
  permissionProps: Omit<PermissionGuardProps, 'children'>
) => {
  return (props: P) => {
    return (
      <PermissionGuard {...permissionProps}>
        <Component {...props} />
      </PermissionGuard>
    );
  };
};